import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { EndClientReportService } from '../end-client-report.service';

/**
 * The images behind one deck's heatmaps, fetched once each and handed out as object URLs.
 *
 * Provided by the report page rather than at the root, because it belongs to one report: the page
 * says which, and the URLs it made are revoked when the page goes. Blocks ask it for their image
 * rather than taking one as an input, since a block is drawn in several places on the page and the
 * image belongs to none of them in particular.
 *
 * Goes through `EndClientReportService`, so the share-link viewer and the theme preview, which each
 * stand in their own, decide for themselves where an image comes from.
 */
@Injectable()
export class HeatmapImageStore implements OnDestroy {
  private reportId = '';
  /** A template's own copies of its pictures, read-only: a template still can't take an upload. */
  private templateId = '';
  private readonly images = new Map<string, Observable<string | null>>();
  private readonly urls: string[] = [];
  /** Ticks when what is stored may have changed, so every block that asked asks again. */
  private readonly refreshed$ = new BehaviorSubject<void>(undefined);

  constructor(private reports: EndClientReportService) { }

  /**
   * Drops what has been fetched. A data refresh can copy in a survey image a block was told did not
   * exist, and without this the block would keep its "none" until the page was reloaded.
   */
  forget() {
    this.images.clear();
    this.refreshed$.next();
  }

  /** Which report the images belong to. */
  setReport(reportId: string) {
    if (reportId === this.reportId) return;
    this.reportId = reportId;
    this.templateId = '';
    this.images.clear();
  }

  /**
   * Which template the images belong to: the pictures copied in when it was saved from a report.
   * Uploaded images only -- a template has no survey of its own to take a question's image from.
   */
  setTemplate(templateId: string) {
    if (templateId === this.templateId) return;
    this.templateId = templateId;
    this.reportId = '';
    this.images.clear();
  }

  /** Whether an image can be uploaded here: only onto a report. */
  get canUpload(): boolean {
    return !!this.reportId;
  }

  /** The uploaded image where the block names one, otherwise the survey's own. Null for neither. */
  imageFor(questionLabel: string, imageId?: string): Observable<string | null> {
    return this.refreshed$.pipe(switchMap(() => this.fetched(questionLabel, imageId)));
  }

  private fetched(questionLabel: string, imageId?: string): Observable<string | null> {
    if (this.templateId) {
      if (!imageId) return of(null);
      const key = `template:${imageId}`;
      let image = this.images.get(key);
      if (!image) {
        image = this.reports.getTemplateImage(this.templateId, imageId)
          .pipe(map(found => this.urlOf(found)), shareReplay(1));
        this.images.set(key, image);
      }
      return image;
    }
    if (!this.reportId || (!imageId && !questionLabel)) return of(null);

    const key = imageId ? `upload:${imageId}` : `question:${questionLabel}`;
    let image = this.images.get(key);
    if (!image) {
      const blob = imageId
        ? this.reports.getUploadedImage(this.reportId, imageId)
        : this.reports.getQuestionImage(this.reportId, questionLabel);
      image = blob.pipe(map(found => this.urlOf(found)), shareReplay(1));
      this.images.set(key, image);
    }
    return image;
  }

  /** Stores an image for one of this report's heatmaps, returning the id the block names it by. */
  upload(file: File): Observable<string> {
    return this.reports.uploadHeatmapImage(this.reportId, file);
  }

  ngOnDestroy() {
    this.urls.forEach(url => URL.revokeObjectURL(url));
  }

  private urlOf(blob: Blob | null): string | null {
    if (!blob || !blob.size) return null;
    const url = URL.createObjectURL(blob);
    this.urls.push(url);
    return url;
  }
}
